import { APOS, BANK_OF_AMERICA, REG } from '@tamman/ui-utils'
import { ExternalLink, Heading, Paragraph } from '@tamman/ui-core'
import { Box } from '@tamman/ui-layout'
import { DialogPortal } from '@tamman/ui-modal'

const EnglishPage = () => {
  return (
    <div>
      <Heading level='one' py='lg'>
        Employee Banking and Investing Program Disclosure Page/FAQs
      </Heading>
      <Heading level='two' py='lg'>
        Program Enrollment
      </Heading>
      <Box className='section'>
        <Paragraph className='question'>
          How do I qualify for the Employee Banking and Investing Program with {BANK_OF_AMERICA} Preferred Rewards{REG} benefits?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>As an employee of a company participating in the {BANK_OF_AMERICA} Employee Banking and Investing Program, you are eligible to enroll in the Preferred Rewards program if you:</Paragraph>

          <ul>
            <li>Have an active, eligible {BANK_OF_AMERICA}{REG} personal checking account,</li>
            <li>Receive all or part of your payroll direct deposit from the participating company into an eligible {BANK_OF_AMERICA} checking or savings account.</li>
          </ul>

          <Paragraph>Within 60 days after {BANK_OF_AMERICA} identifies your payroll direct deposit into your eligible personal checking or savings account, you will be eligible to enroll in the Preferred Rewards program with Gold tier benefits without the standard qualifying balance requirement of $20,000. Visit <ExternalLink id='boa-online-banking-link' href='https://www.bankofamerica.com/online-banking/' target='_blank' rel='noopener noreferrer'>Online Banking</ExternalLink> to learn more.</Paragraph>

          <Paragraph>You must complete the enrollment process for Preferred Rewards in order to receive any benefits of the Banking and Investing Program with Preferred Rewards.</Paragraph>

          <Paragraph>Once enrolled in the Preferred Rewards program you may then qualify for the higher tiers based on your balances under the standard program terms.</Paragraph>

          <Paragraph>There is no fee to join or participate.</Paragraph>

          <Paragraph>To learn more about Preferred Rewards, visit <ExternalLink id='boa-pref-rewards-link-1' href='https://bankofamerica.com/preferred-rewards' target='_blank' rel='noopener noreferrer'>bankofamerica.com/preferred-rewards</ExternalLink>.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          Do I need to enroll to become an Employee Banking and Investing Program with Preferred Rewards member?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>Yes, and enrollment is simple once you qualify by having an eligible {BANK_OF_AMERICA} checking account and are receiving all or part of your payroll direct deposit from the participating company into an eligible {BANK_OF_AMERICA} checking or savings account. You can enroll through Online or Mobile Banking or over the phone with a Specialist.</Paragraph>
            
          <Paragraph>There are no fees to join or participate in Preferred Rewards.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          How will I know if I have enrolled in the Employee Banking and Investing Program with Preferred Rewards benefits?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>If you are enrolled, you will see a Preferred Rewards indicator in your Online Banking, Mobile Banking, and periodic personal checking or savings account statement.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          Will I be automatically enrolled in Preferred Rewards by having a payroll direct deposit from a participating company?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>No. You will need to enroll in the Preferred Rewards program to take advantage of your special access to the program with Gold tier benefits.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          What if my payroll direct deposit goes into my {BANK_OF_AMERICA} personal savings account?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>If your payroll direct deposit only goes into your savings account you will be eligible to enroll in the program as long as you also have an eligible checking account.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          How long will it take to get the Preferred Rewards benefits?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>It may take up to 90 days to receive Preferred Rewards benefits. It may take up to 60 days for {BANK_OF_AMERICA} to identify your payroll direct deposit from a participating company into your eligible checking or savings account. Once {BANK_OF_AMERICA} identifies your payroll direct deposit, you will be eligible to enroll in the Preferred Rewards program with Gold tier benefits.</Paragraph>

          <Paragraph>After you enroll, Preferred Rewards benefits become effective within 30 days of your enrollment.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          I opened a {BANK_OF_AMERICA} personal checking and/or savings account and signed up for payroll direct deposit. I am still seeing fees that I thought were waived, what do I do?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>You may still need to take action to enroll in the Preferred Rewards program to receive program benefits.</Paragraph>

          <Paragraph>You qualify for the Employee Banking and Investing Program benefits once you have an open, active, eligible personal checking account, after your payroll direct deposit has been received into your eligible checking or savings account, and you enroll in the Preferred Rewards program, which does not occur immediately after you sign up for payroll direct deposit with a participating company.</Paragraph>

          <Paragraph>In general you will become eligible to enroll in the Preferred Rewards program within 60 days following {BANK_OF_AMERICA}{APOS}s identification of your company payroll direct deposit into your eligible personal {BANK_OF_AMERICA} checking or savings account. Once {BANK_OF_AMERICA} identifies your payroll direct deposit, and you enroll in the Preferred Rewards program, your benefits become effective within 30 days of your enrollment.</Paragraph>

          <Paragraph>If you are still seeing fees that should be waived as part of the benefits of the Gold tier after you have enrolled in Preferred Rewards, please contact {BANK_OF_AMERICA} at <ExternalLink id='boa-phone-link' href='tel:888.383.7200'>888.383.7200</ExternalLink> for assistance.</Paragraph>
        </Box>
      </Box>

      <Heading level='two' py='lg'>
        Eligibility
      </Heading>

      <Box className='section'>
        <Paragraph className='question'>
          Who is eligible for the Employee Banking and Investing Program with Preferred Rewards?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>Full- and part-time active employees of participating companies who have an eligible {BANK_OF_AMERICA} checking account and receive all or part of their payroll direct deposit from the participating company into an eligible {BANK_OF_AMERICA} checking or savings account.</Paragraph>
        </Box>
      </Box>
      <Box className='section'>
        <Paragraph className='question'>
          Does it matter what kind of account my payroll direct deposit goes in to?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>Yes. All or part of your payroll direct deposit from a participating company must go to an eligible personal {BANK_OF_AMERICA} checking or savings account to qualify for the Employee Banking and Investing Program with Preferred Rewards benefits.</Paragraph>
          <Paragraph>Most active {BANK_OF_AMERICA} personal checking or savings accounts where the employee is an owner or co-owner of the personal account receiving the payroll direct deposit qualify. Revocable Trust accounts opened as personal account types when a trustee{APOS}s SSN is used as the Trust{APOS}s TIN will also qualify.</Paragraph>
          <Paragraph>You will not qualify based on direct deposit to small business, commercial, corporate, institution, brokerage and Merrill investment accounts or special ownership accounts (such as accounts on which you are listed as a guardian, administrator, power of attorney, executor, or representative payee).</Paragraph>
        </Box>
      </Box>

      <Heading level='two' py='lg'>
        Direct deposit
      </Heading>

      <Box className='section'>
        <Paragraph className='question'>
          What if I leave the company or stop or redirect my payroll direct deposit?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>The Preferred Rewards program performs an annual review of your eligibility in the month following the anniversary date of your initial enrollment in the Preferred Rewards program. The annual review will confirm whether you still meet the following program eligibility requirements:</Paragraph>
          <ul>
            <li>Have an active, eligible {BANK_OF_AMERICA}{REG} personal checking account,</li>
            <li>Receive all or part of your payroll direct deposit from the participating company into an eligible {BANK_OF_AMERICA} checking or savings account.</li>
          </ul>
        </Box>
      </Box>

      <Box className='section'>
        <Paragraph className='question'>
          What happens if I don{APOS}t meet the standard Preferred Rewards eligibility requirements at my anniversary?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>If you don{APOS}t restore your payroll direct deposit, have an eligible {BANK_OF_AMERICA} personal checking account, or meet the standard balance requirements for Preferred Rewards in the three months (grace period) after your anniversary month, your enrollment may be discontinued.</Paragraph>
          <ul>
            <li>If your enrollment in Preferred Rewards is discontinued, and within the next 24 months you have an eligible {BANK_OF_AMERICA} personal checking account and either a payroll direct deposit from a participating company or you satisfy the standard balance requirements based on tier level, you will automatically be placed back into the program and begin receiving benefits again.</li>
          </ul>
        </Box>
      </Box>

      <Box className='section'>
        <Paragraph className='question'>
          What if my employer stops participating in the Employee Banking and Investing Program with Preferred Rewards benefits?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>If you{APOS}re already enrolled in the Preferred Rewards program, the balance requirement waiver will continue through the Preferred Rewards annual review of your eligibility in the month following the anniversary date of your initial enrollment in the Preferred Rewards program. If you don{APOS}t have a qualifying payroll direct deposit from another participating company in the {BANK_OF_AMERICA} Banking and Investing Program, have an eligible {BANK_OF_AMERICA} personal checking account, or meet the standard balance requirements for Preferred Rewards in the three months (grace period) after your anniversary month, your enrollment may be discontinued. You can maintain enrollment by meeting the standard account and balance requirements. To learn more, visit <ExternalLink  id='boa-pref-rewards-link-2' href='https://bankofamerica.com/preferred-rewards' target='_blank' rel='noopener noreferrer'>bankofamerica.com/preferred-rewards</ExternalLink>.</Paragraph>
        </Box>
      </Box>

      <Heading level='two' py='lg'>
        Other questions
      </Heading>

      <Box className='section'>
        <Paragraph className='question'>
          How do I qualify for the other tiers of the Preferred Rewards program?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>To qualify for the higher tiers of the Preferred Rewards program, you would need to meet the balance requirements under the standard terms. To ensure that you receive the correct level of benefits and rewards as your assets grow, tier upgrades will be processed automatically every month.</Paragraph>
          <Paragraph>To learn more, visit <ExternalLink  id='boa-pref-rewards-link-3' href='https://bankofamerica.com/preferred-rewards' target='_blank' rel='noopener noreferrer'>bankofamerica.com/preferred-rewards</ExternalLink>.</Paragraph>
        </Box>
      </Box>

      <Box className='section'>
        <Paragraph className='question'>
          What are the standard terms to qualify for the Preferred Rewards program?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>Under the standard terms, you can qualify to enroll in the Preferred Rewards program if you have an active, eligible {BANK_OF_AMERICA}{REG} personal checking account and maintain a qualifying three-month combined average daily balance of at least $20,000 for the Gold tier, $50,000 for the Platinum tier, $100,000 for the Platinum Honors tier, $1,000,000 for the Diamond tier and $10,000,000 for the Diamond Honors tier in your combined qualifying {BANK_OF_AMERICA} deposit accounts (such as checking, savings, certificate of deposit) and/or your Merrill investment accounts (such as Cash Management Accounts, 529 Plans). The qualifying balance is calculated based on your combined average daily balance for a three calendar month period. Benefits become effective within 30 days of your enrollment, or for new accounts within 30 days of account opening, unless we indicate otherwise.</Paragraph>
          <Paragraph>To learn more, visit <ExternalLink  id='boa-pref-rewards-link-4' href='https://bankofamerica.com/preferred-rewards' target='_blank' rel='noopener noreferrer'>bankofamerica.com/preferred-rewards</ExternalLink>.</Paragraph>
        </Box>
      </Box>

      <Box className='section'>
        <Paragraph className='question'>
          I am already enrolled in Preferred Rewards, will this program affect my current Preferred Rewards program status?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>No. If you are already enrolled in a Preferred Rewards tier higher than the Gold tier, you will maintain those benefits on the standard terms. If you no longer meet balance requirements for your current tier, but you continue to be in the Employee Banking and Investing Program and to have your payroll direct deposit into an eligible {BANK_OF_AMERICA} checking or savings account, you can still qualify for the Gold tier benefits.</Paragraph>
        </Box>
      </Box>

      <Box className='section'>
        <Paragraph className='question'>
          I have additional questions about the program; whom can I speak with?
        </Paragraph>
        <Box className='answer'>
          <Paragraph>There is a dedicated team of specialists to help you with any questions. To speak to a specialist, call <ExternalLink id='boa-phone-link-2' href='tel:888.383.7200'>888.383.7200</ExternalLink>.</Paragraph>
        </Box>
      </Box>
      <DialogPortal />
    </div>
  )
}

export default EnglishPage